import { 
  collection, 
  query, 
  where, 
  getDocs, 
  addDoc, 
  updateDoc, 
  deleteDoc,
  doc,
  Timestamp,
  orderBy,
  limit,
  startAfter,
  QueryConstraint,
  serverTimestamp,
  getDoc,
  writeBatch
} from 'firebase/firestore';
import { db } from '../../config/firebase';
import { PaginationOptions, PaginatedResult } from '../../types/pagination';
import { Task, TaskStatus } from '../../types/task';
export type { Task, TaskStatus };
import { TaskCategory } from './taskTemplates.service';
import { WeddingType, taskTemplateService } from './taskTemplates.service';
import { notificationService } from './notification.service';
import { autoNotificationService } from './auto-notification.service';

export type TaskPriority = 'low' | 'medium' | 'high';

export interface TaskTemplate {
  id: string;
  title: string;
  description?: string;
  category: string;
  priority: TaskPriority;
  timeframe: number; // Nombre de jours avant le mariage
  weddingTypes: WeddingType[];
  defaultAssignees?: string[];
}

// Templates prédéfinis pour différents types de mariage
export const TASK_TEMPLATES: TaskTemplate[] = [
  // Mariage Traditionnel - 12 mois avant
  {
    id: 'traditional-venue',
    title: 'Réserver le lieu de réception',
    description: 'Visiter et réserver le lieu pour la cérémonie et la réception',
    category: 'venue',
    priority: 'high',
    timeframe: 365,
    weddingTypes: ['traditional', 'intimate']
  },
  {
    id: 'traditional-budget',
    title: 'Définir le budget global',
    description: 'Établir le budget détaillé par catégorie',
    category: 'budget',
    priority: 'high',
    timeframe: 365,
    weddingTypes: ['traditional', 'intimate', 'destination', 'eco']
  },
  // 9 mois avant
  {
    id: 'traditional-attire',
    title: 'Commander les tenues',
    description: 'Choisir et commander les tenues des mariés',
    category: 'attire',
    priority: 'high',
    timeframe: 270,
    weddingTypes: ['traditional', 'intimate']
  },
  // 6 mois avant
  {
    id: 'traditional-invitations',
    title: 'Commander les faire-part',
    description: 'Concevoir et commander les faire-part',
    category: 'stationery',
    priority: 'medium',
    timeframe: 180,
    weddingTypes: ['traditional', 'intimate']
  },
  // Mariage Éco-responsable
  {
    id: 'eco-local-vendors',
    title: 'Rechercher des prestataires locaux',
    description: 'Identifier des prestataires locaux et éco-responsables',
    category: 'vendors',
    priority: 'high',
    timeframe: 300,
    weddingTypes: ['eco']
  },
  // Destination Wedding
  {
    id: 'destination-travel',
    title: 'Organiser les voyages',
    description: 'Planifier les transports et hébergements',
    category: 'travel',
    priority: 'high',
    timeframe: 270,
    weddingTypes: ['destination']
  }
];

export const tasksService = {
  /**
   * Crée les tâches initiales pour un nouveau mariage
   * @param weddingId ID du mariage
   * @param weddingDate Date du mariage
   * @param weddingType Type de mariage
   */
  async createInitialTasks(weddingId: string, weddingDate: Date, weddingType: WeddingType, language: string = 'fr'): Promise<void> {
    // // // // // // // // // // // // // // // // console.log('[DEBUG] Début de createInitialTasks:', { weddingId, weddingDate, weddingType });

    try {
      // Vérifier si des tâches existent déjà pour ce mariage
      const existingTasks = await this.getTasks(weddingId);
      if (existingTasks.length > 0) {
        // // // // // // // // // // // // // // // // console.log('[DEBUG] Des tâches existent déjà pour ce mariage');
        return;
      }

      // Générer les tâches à partir des templates
      const tasks = taskTemplateService.generateTranslatedTasks(weddingDate, weddingType, language);
      // // // // // // // // // // // // // // // // console.log('[DEBUG] Tâches générées:', tasks.length);

      const tasksCollection = collection(db, 'tasks');
      const batch = writeBatch(db);

      // Créer toutes les tâches dans un batch
      tasks.forEach(task => {
        const taskRef = doc(tasksCollection);
        batch.set(taskRef, {
          ...task,
          id: taskRef.id, // Utiliser l'ID Firestore
          weddingId,
          dueDate: Timestamp.fromDate(task.dueDate),
          createdAt: Timestamp.fromDate(task.createdAt),
          updatedAt: Timestamp.fromDate(task.updatedAt)
        });
      });

      // Exécuter le batch
      await batch.commit();
      // // // // // // // // // // // // // // // // console.log('[DEBUG] Tâches initiales créées avec succès');
    } catch (error) {
      console.error('[DEBUG] Erreur lors de la création des tâches initiales:', error);
      throw error;
    }
  },

  /**
   * Récupère toutes les tâches d'un mariage
   * @param weddingId ID du mariage
   */
  async getTasks(weddingId: string): Promise<Task[]> {
    const tasksCollection = collection(db, 'tasks');
    const q = query(
      tasksCollection,
      where('weddingId', '==', weddingId),
      orderBy('dueDate', 'asc')
    );

    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        dueDate: data['dueDate'].toDate(),
        createdAt: data['createdAt'].toDate(),
        updatedAt: data['updatedAt'].toDate()
      } as Task;
    });
  },

  /**
   * Met à jour le statut d'une tâche
   * @param taskId ID de la tâche
   * @param status Nouveau statut
   */
  async updateTaskStatus(taskId: string, status: TaskStatus): Promise<void> {
    try {
      // Récupérer la tâche actuelle pour connaître son statut précédent
      const taskRef = doc(db, 'tasks', taskId);
      const taskDoc = await getDoc(taskRef);
      
      if (!taskDoc.exists()) {
        throw new Error(`La tâche ${taskId} n'existe pas`);
      }
      
      const task = taskDoc.data() as Task;
      const oldStatus = task.status;
      
      // Mettre à jour le statut
      await updateDoc(taskRef, {
        status,
        updatedAt: Timestamp.now()
      });

      // Générer une notification pour le changement de statut
      if (task.weddingId) {
        await autoNotificationService.notifyTaskStatusChange(
          task.weddingId,
          { ...task, id: taskId },
          oldStatus,
          status
        );
      }
    } catch (error) {
      console.error('Failed to update task status:', error);
      throw new Error('Failed to update task status. Please try again.');
    }
  },

  /**
   * Met à jour une tâche
   * @param taskId ID de la tâche
   * @param updates Modifications à appliquer
   */
  async updateTask(taskId: string, updates: Partial<Task>): Promise<void> {
    const taskRef = doc(db, 'tasks', taskId);
    
    // Vérifier si le document existe
    const taskDoc = await getDoc(taskRef);
    if (!taskDoc.exists()) {
      throw new Error(`La tâche ${taskId} n'existe pas`);
    }
    
    const task = taskDoc.data() as Task;
    const oldStatus = task.status;

    const updatesWithTimestamp = {
      ...updates,
      updatedAt: Timestamp.now(),
      ...(updates.dueDate && { dueDate: Timestamp.fromDate(updates.dueDate) }),
    };

    await updateDoc(taskRef, updatesWithTimestamp);
    
    // Si le statut a changé, générer une notification
    if (updates.status && updates.status !== oldStatus && task.weddingId) {
      await autoNotificationService.notifyTaskStatusChange(
        task.weddingId,
        { ...task, ...updates, id: taskId },
        oldStatus,
        updates.status
      );
    }
  },

  /**
   * Supprime une tâche
   * @param taskId ID de la tâche
   */
  async deleteTask(taskId: string): Promise<void> {
    const taskRef = doc(db, 'tasks', taskId);
    await deleteDoc(taskRef);
  },

  async getPaginatedTasks(
    weddingId: string,
    paginationOptions: PaginationOptions,
    filters?: {
      status?: TaskStatus;
      category?: string;
      priority?: TaskPriority;
      startDate?: Date;
      endDate?: Date;
    },
    sort?: { field: string; direction: 'asc' | 'desc' }
  ): Promise<PaginatedResult<Task>> {
    try {
      const constraints: QueryConstraint[] = [
        where('weddingId', '==', weddingId)
      ];

      if (filters) {
        if (filters.status) {
          constraints.push(where('status', '==', filters.status));
        }
        if (filters.category) {
          constraints.push(where('category', '==', filters.category));
        }
        if (filters.priority) {
          constraints.push(where('priority', '==', filters.priority));
        }
        if (filters.startDate) {
          constraints.push(where('dueDate', '>=', Timestamp.fromDate(filters.startDate)));
        }
        if (filters.endDate) {
          constraints.push(where('dueDate', '<=', Timestamp.fromDate(filters.endDate)));
        }
      }

      constraints.push(orderBy(sort?.field || 'dueDate', sort?.direction || 'asc'));
      constraints.push(limit(paginationOptions.limit));

      if (paginationOptions.startAfter) {
        constraints.push(startAfter(paginationOptions.startAfter));
      }

      const tasksQuery = query(collection(db, 'tasks'), ...constraints);
      const querySnapshot = await getDocs(tasksQuery);

      const tasks = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          title: data['title'] || '',
          description: data['description'] || '',
          status: data['status'] || 'todo',
          priority: data['priority'] || 'medium',
          category: data['category'] || 'general',
          assignees: data['assignees'] || [],
          attachments: data['attachments'] || [],
          dueDate: data['dueDate'].toDate(),
          createdAt: data['createdAt'].toDate(),
          updatedAt: data['updatedAt'].toDate(),
          completedAt: data['completedAt']?.toDate(),
          reminderDate: data['reminderDate']?.toDate()
        } as Task;
      });

      return {
        items: tasks,
        lastDoc: querySnapshot.docs[querySnapshot.docs.length - 1] || null,
        hasMore: querySnapshot.docs.length === paginationOptions.limit
      };
    } catch (error) {
      console.error('Erreur lors de la récupération des tâches:', error);
      throw error;
    }
  },

  async createTaskFromTemplate(
    weddingId: string,
    templateId: string,
    weddingDate: Date,
    customData?: Partial<Task>
  ): Promise<Task> {
    try {
      const template = TASK_TEMPLATES.find(t => t.id === templateId);
      if (!template) {
        throw new Error('Template non trouvé');
      }

      const dueDate = new Date(weddingDate);
      dueDate.setDate(dueDate.getDate() - template.timeframe);

      const now = new Date();
      const taskData = {
        weddingId,
        title: template.title,
        description: template.description || '',
        category: template.category as TaskCategory,
        priority: template.priority,
        status: 'todo' as TaskStatus,
        dueDate: Timestamp.fromDate(dueDate),
        assignees: template.defaultAssignees || [],
        templateId,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
        ...customData
      };

      const docRef = await addDoc(collection(db, 'tasks'), taskData);
      return {
        id: docRef.id,
        ...taskData,
        dueDate,
        createdAt: now,
        updatedAt: now
      };
    } catch (error) {
      console.error('Erreur lors de la création de la tâche:', error);
      throw error;
    }
  },

  async createTasksFromWeddingType(
    weddingId: string,
    weddingType: WeddingType,
    weddingDate: Date
  ): Promise<Task[]> {
    try {
      const templates = TASK_TEMPLATES.filter(t => t.weddingTypes.includes(weddingType));
      const tasks = await Promise.all(
        templates.map(template => 
          this.createTaskFromTemplate(weddingId, template.id, weddingDate)
        )
      );
      return tasks;
    } catch (error) {
      console.error('Erreur lors de la création des tâches:', error);
      throw error;
    }
  },

  async addTask(weddingId: string, taskData: Omit<Task, 'id' | 'weddingId' | 'createdAt' | 'updatedAt'>) {
    try {
      const now = new Date();
      const docRef = await addDoc(collection(db, 'tasks'), {
        ...taskData,
        weddingId,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
        dueDate: Timestamp.fromDate(taskData.dueDate)
      });

      return {
        id: docRef.id,
        ...taskData,
        weddingId,
        createdAt: now,
        updatedAt: now
      };
    } catch (error) {
      console.error('Erreur lors de l\'ajout de la tâche:', error);
      throw error;
    }
  },

  async toggleTaskComplete(taskId: string, completed: boolean) {
    return this.updateTask(taskId, { status: completed ? 'done' : 'todo' });
  },

  async createTask(task: Omit<Task, 'id' | 'createdAt' | 'updatedAt'>): Promise<string> {
    const docRef = await addDoc(collection(db, 'tasks'), {
      ...task,
      dueDate: Timestamp.fromDate(task.dueDate),
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp()
    });

    // Créer une notification pour la tâche
    const dueDate = task.dueDate;
    const today = new Date();
    const threeDaysBefore = new Date(dueDate);
    threeDaysBefore.setDate(dueDate.getDate() - 3);

    // Si la date limite est dans plus de 3 jours, créer une notification pour 3 jours avant
    if (threeDaysBefore > today && task.weddingId) {
      await notificationService.createNotification({
        userId: task.weddingId, // Utiliser le weddingId comme userId car les notifications sont par mariage
        weddingId: task.weddingId,
        title: `Rappel : ${task.title}`,
        message: `La tâche "${task.title}" doit être terminée dans 3 jours`,
        type: 'task',
        category: 'tasks'
      });
    }

    // Créer une notification pour le jour même
    if (task.weddingId) {
      await notificationService.createNotification({
        userId: task.weddingId,
        weddingId: task.weddingId,
        title: `Échéance : ${task.title}`,
        message: `La tâche "${task.title}" doit être terminée aujourd'hui`,
        type: 'task',
        category: 'tasks'
      });
    }

    return docRef.id;
  },

  // Auto-compléter certaines tâches en fonction des mises à jour de l'événement
  async autoCompleteTasks(weddingId: string, updates: any): Promise<void> {
    try {
      const tasks = await this.getTasks(weddingId);
      
      const tasksToComplete: string[] = [];

      // Si la date est mise à jour et n'est pas nulle
      if (updates.date !== undefined && updates.date !== null) {
        const dateTasks = tasks.filter(t => 
          t.title.toLowerCase().includes('date') && 
          t.status !== 'done'
        );
        dateTasks.forEach(t => {
          if (t.id && !tasksToComplete.includes(t.id)) tasksToComplete.push(t.id);
        });
      }

      // Si le lieu est mis à jour et n'est pas vide
      if (updates.location !== undefined && updates.location !== null && updates.location !== '') {
        const venueTasks = tasks.filter(t => 
          (t.title.toLowerCase().includes('lieu') || t.title.toLowerCase().includes('endroit')) && 
          t.status !== 'done'
        );
        venueTasks.forEach(t => {
          if (t.id && !tasksToComplete.includes(t.id)) tasksToComplete.push(t.id);
        });
      }

      // Mettre à jour les tâches trouvées
      for (const taskId of tasksToComplete) {
        await this.updateTask(taskId, {
          status: 'done'
        });
      }
    } catch (error) {
      console.error('[DEBUG] Erreur lors de l\'auto-complétion des tâches:', error);
    }
  },

  // Auto-compléter des tâches spécifiques en fonction de mots-clés (ex: ajout d'un invité -> "Établir la liste des invités")
  async completeTasksByKeywords(weddingId: string, keywords: string[]): Promise<void> {
    try {
      const tasks = await this.getTasks(weddingId);
      
      const tasksToComplete = tasks.filter(t => 
        t.status !== 'done' && 
        keywords.some(kw => t.title.toLowerCase().includes(kw.toLowerCase()))
      );

      for (const t of tasksToComplete) {
        if (t.id) {
          await this.updateTask(t.id, {
            status: 'done'
          });
        }
      }
    } catch (error) {
      console.error('[DEBUG] Erreur lors de l\'auto-complétion par mots-clés:', error);
    }
  }
}; 